import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, RefreshCw, AlertCircle, CheckCircle, Clock,
  DollarSign, Wallet, CreditCard, PieChart, FileText, 
  Calendar, TrendingUp, TrendingDown, Activity, AlertTriangle,
  ChevronDown, ChevronUp, Eye, Download, Play
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { API_URL } from '../../utils/apiConfig';

export default function AdminTreasuryView() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [position, setPosition] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [reconciliationReports, setReconciliationReports] = useState([]);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [activeTab, setActiveTab] = useState('position');
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  const [runningReconciliation, setRunningReconciliation] = useState(false);
  const [expandedSnapshot, setExpandedSnapshot] = useState(null);
  const [expandedReport, setExpandedReport] = useState(null);

  const getToken = () => localStorage.getItem('pecash_token');
  const isAdmin = localStorage.getItem('pecash_admin') === 'true';

  const fetchTreasuryData = useCallback(async () => {
    const token = getToken();
    
    if (!token || !isAdmin) {
      navigate('/admin');
      return;
    }

    try {
      setLoading(true);
      
      // Fetch position
      const positionRes = await fetch(`${API_URL}/api/admin/treasury/position`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!positionRes.ok) {
        if (positionRes.status === 401 || positionRes.status === 403) {
          throw new Error('Session expired');
        }
        throw new Error('Failed to fetch treasury position');
      }
      
      const positionData = await positionRes.json();
      setPosition(positionData);

      // Fetch snapshots
      const snapshotsRes = await fetch(`${API_URL}/api/admin/treasury/snapshots?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (snapshotsRes.ok) {
        const snapshotsData = await snapshotsRes.json();
        setSnapshots(snapshotsData.snapshots || []);
      }

      // Fetch reconciliation reports
      const reportsRes = await fetch(`${API_URL}/api/admin/treasury/reconciliation/reports?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (reportsRes.ok) {
        const reportsData = await reportsRes.json();
        setReconciliationReports(reportsData.reports || []);
      }

      // Fetch ledger entries
      const ledgerRes = await fetch(`${API_URL}/api/admin/treasury/ledger/entries?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (ledgerRes.ok) {
        const ledgerData = await ledgerRes.json();
        setLedgerEntries(ledgerData.entries || []);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      if (err.message === 'Session expired') {
        localStorage.removeItem('pecash_token');
        localStorage.removeItem('pecash_admin');
        setTimeout(() => navigate('/admin'), 2000);
      }
    }
  }, [navigate, isAdmin]);

  useEffect(() => {
    fetchTreasuryData();
  }, [fetchTreasuryData]);

  const handleCreateSnapshot = async (type = 'manual') => {
    const token = getToken();
    setCreatingSnapshot(true);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/treasury/snapshots/create?snapshot_type=${type}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to create snapshot');
      }
      
      await fetchTreasuryData();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingSnapshot(false);
    }
  };

  const handleRunReconciliation = async (periodDays = 7) => {
    const token = getToken();
    setRunningReconciliation(true);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/treasury/reconciliation/run?period_days=${periodDays}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to run reconciliation');
      }
      
      await fetchTreasuryData();
    } catch (err) {
      setError(err.message);
    } finally {
      setRunningReconciliation(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'balanced': return 'text-emerald-400 bg-emerald-900/30';
      case 'discrepancy': return 'text-red-400 bg-red-900/30';
      case 'pending': return 'text-amber-400 bg-amber-900/30';
      case 'requires_review': return 'text-orange-400 bg-orange-900/30';
      default: return 'text-slate-400 bg-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading treasury data...</p>
        </div>
      </div>
    );
  }

  if (error && error === 'Session expired') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center bg-slate-800 rounded-2xl p-8 border border-red-800">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Session Expired</h2>
          <p className="text-red-400 mb-4">{error}</p>
          <p className="text-slate-500 text-sm">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="ghost"
              className="text-slate-400 hover:text-white"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Treasury & Reconciliation</h1>
              <p className="text-sm text-slate-400">Financial Source of Truth</p>
            </div>
          </div>
          
          <Button
            onClick={fetchTreasuryData}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            data-testid="refresh-treasury"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      {/* Error Banner */}
      {error && error !== 'Session expired' && (
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-4">
            <p className="text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Pilot Mode Banner */}
        {position?.pilot_mode && (
          <div className="bg-amber-900/20 border border-amber-800/50 rounded-xl p-4 mb-6">
            <p className="text-amber-300 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              PILOT MODE - All financial data is simulated. No real money movement.
            </p>
          </div>
        )}

        {/* Position Summary Cards */}
        {position && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6"
              data-testid="platform-holds-card"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-blue-900/50 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-slate-400 text-sm">Platform Holds</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {formatCurrency(position.position_summary.platform_holds_usd)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Sender wallets + Outstanding auths
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6"
              data-testid="platform-owes-card"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-red-900/50 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-red-400" />
                </div>
                <span className="text-slate-400 text-sm">Platform Owes</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {formatCurrency(position.position_summary.platform_owes_usd)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Merchant balances + Pending settlements
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6"
              data-testid="net-position-card"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  position.position_summary.net_position_usd >= 0 
                    ? 'bg-emerald-900/50' 
                    : 'bg-red-900/50'
                }`}>
                  {position.position_summary.net_position_usd >= 0 
                    ? <TrendingUp className="w-5 h-5 text-emerald-400" />
                    : <TrendingDown className="w-5 h-5 text-red-400" />
                  }
                </div>
                <span className="text-slate-400 text-sm">Net Position</span>
              </div>
              <p className={`text-2xl font-bold ${
                position.position_summary.net_position_usd >= 0 
                  ? 'text-emerald-400' 
                  : 'text-red-400'
              }`}>
                {formatCurrency(position.position_summary.net_position_usd)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Holds - Owes = Net
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6"
              data-testid="fx-rate-card"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-purple-900/50 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-purple-400" />
                </div>
                <span className="text-slate-400 text-sm">FX Rate</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {position.fx_rate.toFixed(2)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                USD → DOP
              </p>
            </motion.div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
          {[
            { id: 'position', label: 'Position Details', icon: PieChart },
            { id: 'snapshots', label: 'Snapshots', icon: Calendar },
            { id: 'reconciliation', label: 'Reconciliation', icon: CheckCircle },
            { id: 'ledger', label: 'Ledger', icon: FileText }
          ].map((tab) => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              className={activeTab === tab.id 
                ? 'bg-purple-600 text-white' 
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'position' && position && (
          <div className="space-y-6">
            {/* Wallet Breakdowns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-blue-400" />
                  Sender Wallets
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Total Balance</span>
                    <span className="text-white font-medium">
                      {formatCurrency(position.sender_wallets.total_balance_usd)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Wallet Count</span>
                    <span className="text-white font-medium">
                      {position.sender_wallets.wallet_count}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-emerald-400" />
                  Merchant Wallets
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Total Balance</span>
                    <span className="text-white font-medium">
                      {formatCurrency(position.merchant_wallets.total_balance_usd)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Wallet Count</span>
                    <span className="text-white font-medium">
                      {position.merchant_wallets.wallet_count}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Authorizations & Settlements */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-400" />
                  Outstanding Authorizations
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Remaining Value (USD)</span>
                    <span className="text-white font-medium">
                      {formatCurrency(position.outstanding_authorizations.remaining_value_usd)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Remaining Value (DOP)</span>
                    <span className="text-white font-medium">
                      RD${position.outstanding_authorizations.remaining_value_dop.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Active Count</span>
                    <span className="text-white font-medium">
                      {position.outstanding_authorizations.count}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-purple-400" />
                  Settlements
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Pending Settlement</span>
                    <span className="text-amber-400 font-medium">
                      {formatCurrency(position.pending_settlements.total_usd)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Completed Settlement</span>
                    <span className="text-emerald-400 font-medium">
                      {formatCurrency(position.completed_settlements.total_usd)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction Stats */}
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-400" />
                Transaction Statistics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-slate-400 text-sm">Total</p>
                  <p className="text-white text-2xl font-bold">{position.transaction_stats.total}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Successful</p>
                  <p className="text-emerald-400 text-2xl font-bold">{position.transaction_stats.successful}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Failed</p>
                  <p className="text-red-400 text-2xl font-bold">{position.transaction_stats.failed}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Success Rate</p>
                  <p className="text-blue-400 text-2xl font-bold">{position.transaction_stats.success_rate}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'snapshots' && (
          <div className="space-y-6">
            {/* Actions */}
            <div className="flex gap-4">
              <Button
                onClick={() => handleCreateSnapshot('daily')}
                disabled={creatingSnapshot}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="create-daily-snapshot"
              >
                {creatingSnapshot ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Calendar className="w-4 h-4 mr-2" />
                )}
                Create Daily Snapshot
              </Button>
              <Button
                onClick={() => handleCreateSnapshot('manual')}
                disabled={creatingSnapshot}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="create-manual-snapshot"
              >
                Create Manual Snapshot
              </Button>
            </div>

            {/* Snapshots List */}
            <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-lg font-semibold text-white">Balance Snapshots</h3>
                <p className="text-sm text-slate-400">Immutable audit checkpoints</p>
              </div>
              
              {snapshots.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  No snapshots yet. Create your first snapshot to establish a baseline.
                </div>
              ) : (
                <div className="divide-y divide-slate-700">
                  {snapshots.map((snapshot) => (
                    <div key={snapshot.id} className="p-4">
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setExpandedSnapshot(
                          expandedSnapshot === snapshot.id ? null : snapshot.id
                        )}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-slate-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{snapshot.snapshot_date}</p>
                            <p className="text-sm text-slate-400 capitalize">{snapshot.snapshot_type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-2 py-1 rounded text-xs ${getStatusColor(snapshot.reconciliation_status)}`}>
                            {snapshot.reconciliation_status}
                          </span>
                          <span className="text-white font-medium">
                            {formatCurrency(snapshot.sender_wallets_usd + snapshot.merchant_wallets_usd)}
                          </span>
                          {expandedSnapshot === snapshot.id 
                            ? <ChevronUp className="w-5 h-5 text-slate-400" />
                            : <ChevronDown className="w-5 h-5 text-slate-400" />
                          }
                        </div>
                      </div>
                      
                      {expandedSnapshot === snapshot.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-4 pt-4 border-t border-slate-700"
                        >
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-slate-400">Sender Wallets</p>
                              <p className="text-white">{formatCurrency(snapshot.sender_wallets_usd)}</p>
                            </div>
                            <div>
                              <p className="text-slate-400">Merchant Wallets</p>
                              <p className="text-white">{formatCurrency(snapshot.merchant_wallets_usd)}</p>
                            </div>
                            <div>
                              <p className="text-slate-400">Outstanding Auths</p>
                              <p className="text-white">{formatCurrency(snapshot.outstanding_auth_usd)}</p>
                            </div>
                            <div>
                              <p className="text-slate-400">Pending Settlement</p>
                              <p className="text-white">{formatCurrency(snapshot.pending_settlement_usd)}</p>
                            </div>
                            <div>
                              <p className="text-slate-400">FX Rate</p>
                              <p className="text-white">{snapshot.fx_rate_at_snapshot}</p>
                            </div>
                            <div>
                              <p className="text-slate-400">Transactions</p>
                              <p className="text-white">{snapshot.transactions_count}</p>
                            </div>
                            <div>
                              <p className="text-slate-400">Created At</p>
                              <p className="text-white">{formatDate(snapshot.created_at)}</p>
                            </div>
                            <div>
                              <p className="text-slate-400">Notes</p>
                              <p className="text-white">{snapshot.reconciliation_notes || 'None'}</p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'reconciliation' && (
          <div className="space-y-6">
            {/* Actions */}
            <div className="flex gap-4">
              <Button
                onClick={() => handleRunReconciliation(7)}
                disabled={runningReconciliation}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="run-reconciliation-7d"
              >
                {runningReconciliation ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Run 7-Day Reconciliation
              </Button>
              <Button
                onClick={() => handleRunReconciliation(30)}
                disabled={runningReconciliation}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="run-reconciliation-30d"
              >
                Run 30-Day Reconciliation
              </Button>
            </div>

            {/* Reconciliation Reports */}
            <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-lg font-semibold text-white">Reconciliation Reports</h3>
                <p className="text-sm text-slate-400">Financial integrity verification</p>
              </div>
              
              {reconciliationReports.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  No reconciliation reports yet. Run your first reconciliation check.
                </div>
              ) : (
                <div className="divide-y divide-slate-700">
                  {reconciliationReports.map((report) => (
                    <div key={report.id} className="p-4">
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setExpandedReport(
                          expandedReport === report.id ? null : report.id
                        )}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            report.status === 'balanced' 
                              ? 'bg-emerald-900/50' 
                              : 'bg-red-900/50'
                          }`}>
                            {report.status === 'balanced' 
                              ? <CheckCircle className="w-5 h-5 text-emerald-400" />
                              : <AlertCircle className="w-5 h-5 text-red-400" />
                            }
                          </div>
                          <div>
                            <p className="text-white font-medium">{report.report_date}</p>
                            <p className="text-sm text-slate-400">
                              Period: {new Date(report.period_start).toLocaleDateString()} - {new Date(report.period_end).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-2 py-1 rounded text-xs ${getStatusColor(report.status)}`}>
                            {report.status}
                          </span>
                          {expandedReport === report.id 
                            ? <ChevronUp className="w-5 h-5 text-slate-400" />
                            : <ChevronDown className="w-5 h-5 text-slate-400" />
                          }
                        </div>
                      </div>
                      
                      {expandedReport === report.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-4 pt-4 border-t border-slate-700"
                        >
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mb-4">
                            <div>
                              <p className="text-slate-400">Sender Balance</p>
                              <p className="text-white">{formatCurrency(report.sender_balance_usd || 0)}</p>
                            </div>
                            <div>
                              <p className="text-slate-400">Merchant Balance</p>
                              <p className="text-white">{formatCurrency(report.merchant_balance_usd || 0)}</p>
                            </div>
                            <div>
                              <p className="text-slate-400">Requires Action</p>
                              <p className={report.requires_action ? 'text-red-400' : 'text-emerald-400'}>
                                {report.requires_action ? 'Yes' : 'No'}
                              </p>
                            </div>
                          </div>
                          
                          {report.action_items && report.action_items.length > 0 && (
                            <div className="bg-slate-900 rounded-lg p-4">
                              <p className="text-amber-400 font-medium mb-2">Action Items:</p>
                              <ul className="space-y-1 text-sm text-slate-300">
                                {report.action_items.map((item, idx) => (
                                  <li key={idx} className="font-mono">{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'ledger' && (
          <div className="space-y-6">
            {/* Ledger Entries Table */}
            <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-lg font-semibold text-white">Ledger Entries</h3>
                <p className="text-sm text-slate-400">Audit trail of all financial movements</p>
              </div>
              
              {ledgerEntries.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  No ledger entries found.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-900">
                      <tr>
                        <th className="text-left p-4 text-slate-400 font-medium">Type</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Entity</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Amount</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Debit</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Credit</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {ledgerEntries.map((entry, idx) => (
                        <tr key={idx} className="hover:bg-slate-700/50">
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              entry.entry_type.includes('created') ? 'bg-blue-900/50 text-blue-400' :
                              entry.entry_type.includes('redeemed') ? 'bg-emerald-900/50 text-emerald-400' :
                              entry.entry_type.includes('released') ? 'bg-amber-900/50 text-amber-400' :
                              'bg-slate-700 text-slate-400'
                            }`}>
                              {entry.entry_type.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="p-4 text-slate-300 font-mono text-xs">
                            {entry.entity_id?.slice(0, 8)}...
                          </td>
                          <td className="p-4 text-white font-medium">
                            {formatCurrency(entry.amount_usd)}
                          </td>
                          <td className="p-4 text-red-400 text-xs font-mono">
                            {entry.debit_account}
                          </td>
                          <td className="p-4 text-emerald-400 text-xs font-mono">
                            {entry.credit_account}
                          </td>
                          <td className="p-4 text-slate-400 text-xs">
                            {formatDate(entry.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
